import { BreadcrumbPayload } from 'types/models/ErrorTracking';
import {
  SessionEvent,
  SessionMetadataInput,
  SessionTimelineEventInput,
} from 'types/session';
import { SessionStateEventType } from 'types/sessionStateLog';
import { getBrowserContext } from './browserContext';
import { SESSION_CONFIG } from './config';
import { isSessionStateExpiredError } from './sessionStateErrors';
import { sendSessionStateEvents } from './sessionStateUploader';
import {
  getForceLogoutReasonFromHeaders,
  isSessionIdentityMismatchMessage,
  triggerClientForceLogout,
} from '@/lib/Auth/clientForceLogout';

export type BufferedEvents = {
  timeline: SessionTimelineEventInput[];
  pageViewDelta: number;
};

export const enqueueTimelineEvent = ({
  buffer,
  recentHistory,
  eventType,
  data,
  occurredAt,
}: {
  buffer: BufferedEvents;
  recentHistory: SessionTimelineEventInput[];
  eventType: SessionTimelineEventInput['eventType'];
  data?: Record<string, unknown>;
  occurredAt?: string;
}) => {
  const event: SessionTimelineEventInput = {
    eventType,
    data,
    occurredAt: occurredAt ?? new Date().toISOString(),
  };

  buffer.timeline.push(event);
  if (buffer.timeline.length > SESSION_CONFIG.TIMELINE_MAX) {
    buffer.timeline.shift();
  }

  recentHistory.push(event);
  if (recentHistory.length > SESSION_CONFIG.TIMELINE_MAX) {
    recentHistory.shift();
  }
};

export const absorbPendingHistory = ({
  recentHistory,
  pendingHistory,
}: {
  recentHistory: SessionTimelineEventInput[];
  pendingHistory: SessionTimelineEventInput[];
}): SessionTimelineEventInput[] =>
  [...recentHistory, ...pendingHistory].slice(-SESSION_CONFIG.TIMELINE_MAX);

export const flushBufferedEvents = (buffer: BufferedEvents): BufferedEvents => {
  const snapshot = {
    timeline: [...buffer.timeline],
    pageViewDelta: buffer.pageViewDelta,
  };
  buffer.timeline = [];
  buffer.pageViewDelta = 0;
  return snapshot;
};

export const restoreBufferedEvents = (
  buffer: BufferedEvents,
  snapshot: BufferedEvents
) => {
  if (snapshot.timeline.length) {
    buffer.timeline = [...snapshot.timeline, ...buffer.timeline].slice(
      -SESSION_CONFIG.TIMELINE_MAX
    );
  }
  buffer.pageViewDelta += snapshot.pageViewDelta;
};

export const setupSessionEventListeners = ({
  listenersAttached,
  beforeUnloadHandler,
  visibilityHandler,
  onBeforeUnload,
  onVisibilityHidden,
}: {
  listenersAttached: boolean;
  beforeUnloadHandler?: () => void;
  visibilityHandler?: () => void;
  onBeforeUnload: () => void;
  onVisibilityHidden: () => void;
}) => {
  if (listenersAttached) {
    return { listenersAttached, beforeUnloadHandler, visibilityHandler };
  }

  const nextBeforeUnloadHandler =
    beforeUnloadHandler ??
    (() => {
      onBeforeUnload();
    });
  const nextVisibilityHandler =
    visibilityHandler ??
    (() => {
      if (document.visibilityState === 'hidden') {
        onVisibilityHidden();
      }
    });

  window.addEventListener('beforeunload', nextBeforeUnloadHandler);
  document.addEventListener('visibilitychange', nextVisibilityHandler);
  return {
    listenersAttached: true,
    beforeUnloadHandler: nextBeforeUnloadHandler,
    visibilityHandler: nextVisibilityHandler,
  };
};

export const detachSessionEventListeners = ({
  listenersAttached,
  beforeUnloadHandler,
  visibilityHandler,
}: {
  listenersAttached: boolean;
  beforeUnloadHandler?: () => void;
  visibilityHandler?: () => void;
}) => {
  if (!listenersAttached) {
    return { listenersAttached, beforeUnloadHandler, visibilityHandler };
  }

  if (beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
  }
  return {
    listenersAttached: false,
    beforeUnloadHandler: undefined,
    visibilityHandler: undefined,
  };
};

export const shouldRotateSessionForUser = ({
  nextUserId,
  storedUserId,
}: {
  nextUserId?: string | null;
  storedUserId?: string | null;
}) => {
  if (nextUserId === null) {
    return typeof storedUserId === 'string';
  }
  if (typeof nextUserId !== 'string') return false;
  if (typeof storedUserId === 'undefined') return true;
  if (storedUserId === null) return true;
  if (typeof storedUserId === 'string' && storedUserId !== nextUserId) {
    return true;
  }
  return false;
};

export const normalizeSessionPath = (value: string): string => {
  if (!value) return value;
  try {
    const parsed = new URL(
      value,
      value.startsWith('http') ? undefined : window.location.origin
    );
    return parsed.pathname;
  } catch {
    return value.split(/[?#]/)[0];
  }
};

export const collectSessionMetadata = (): SessionMetadataInput => ({
  userAgent: navigator.userAgent,
  language: navigator.language,
  timezoneOffset: new Date().getTimezoneOffset(),
});

export const buildSessionBreadcrumbs = ({
  pendingHistory,
  recentHistory,
  bufferedTimeline,
  limit = SESSION_CONFIG.TIMELINE_MAX,
}: {
  pendingHistory: SessionTimelineEventInput[];
  recentHistory: SessionTimelineEventInput[];
  bufferedTimeline: SessionTimelineEventInput[];
  limit?: number;
}): BreadcrumbPayload[] => {
  const combinedHistory = [
    ...pendingHistory,
    ...recentHistory,
    ...bufferedTimeline,
  ].sort(
    (a, b) =>
      new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime()
  );

  return combinedHistory.slice(-limit).map((event) => {
    const data = event.data || {};
    let category: BreadcrumbPayload['category'] = 'user_action';
    let action = 'Unknown action';
    let level: BreadcrumbPayload['level'] = 'info';
    let metadata: Record<string, unknown> | undefined = undefined;

    switch (event.eventType) {
      case 'navigation':
        category = 'navigation';
        action = `Navigated to ${data.pathname}`;
        break;
      case 'api_call':
        category = 'api_call';
        action = `${data.method} ${data.endpoint}`;
        if (typeof data.statusCode === 'number') {
          metadata = { statusCode: data.statusCode };
          if (data.statusCode >= 400) level = 'error';
        }
        break;
      case 'user_action':
        category = 'user_action';
        action = String(data.action);
        if (data.metadata && typeof data.metadata === 'object') {
          metadata = data.metadata as Record<string, unknown>;
        }
        break;
      case 'console':
        category = 'console';
        action = `Console: ${data.message}`;
        if (
          data.level === 'warn' ||
          data.level === 'error' ||
          data.level === 'info'
        ) {
          level = data.level;
        }
        break;
    }

    return {
      action,
      category,
      level,
      metadata,
      occurredAt: event.occurredAt,
    };
  });
};

export const readStoredSession = (): {
  sessionId: string;
  startedAt: string;
  lastSeenAt?: string | null;
  userId?: string | null;
} | null => {
  const stored = sessionStorage.getItem('error-session');
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    if (
      !parsed ||
      typeof parsed.sessionId !== 'string' ||
      typeof parsed.startedAt !== 'string'
    ) {
      return null;
    }
    return {
      sessionId: parsed.sessionId,
      startedAt: parsed.startedAt,
      lastSeenAt:
        typeof parsed.lastSeenAt === 'string' ? parsed.lastSeenAt : undefined,
      userId:
        typeof parsed.userId === 'string'
          ? parsed.userId
          : parsed.userId === null
            ? null
            : undefined,
    };
  } catch {
    return null;
  }
};

export const persistStoredSession = ({
  sessionId,
  startedAt,
  lastSeenAt,
  userId,
}: {
  sessionId: string;
  startedAt: string;
  lastSeenAt?: string;
  userId?: string | null;
}) => {
  sessionStorage.setItem(
    'error-session',
    JSON.stringify({
      sessionId,
      startedAt,
      lastSeenAt,
      ...(typeof userId !== 'undefined' ? { userId } : {}),
    })
  );
};

export const clearStoredSession = () => {
  sessionStorage.removeItem('error-session');
};

export const syncSessionWithServer = async ({
  sessionId,
  currentUserId,
  onServerSessionIdChanged,
  onMarkSeen,
}: {
  sessionId: string;
  currentUserId: string | null | undefined;
  onServerSessionIdChanged: (serverSessionId: string) => void;
  onMarkSeen: (timestamp: string) => void;
}): Promise<void> => {
  try {
    if (!currentUserId) {
      return;
    }

    const response = await fetch('/api/session-state/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        sessionId,
        platform: 'WEB',
        userId: currentUserId ?? undefined,
      }),
    });

    if (!response.ok) {
      const responseBody = await response
        .clone()
        .json()
        .catch(() => null);
      const responseMessage =
        typeof (responseBody as { message?: unknown } | null)?.message ===
        'string'
          ? (responseBody as { message: string }).message
          : undefined;
      const forceLogoutReason = getForceLogoutReasonFromHeaders(
        response.headers
      );
      const shouldForceLogout =
        Boolean(forceLogoutReason) ||
        response.status === 401 ||
        isSessionIdentityMismatchMessage(responseMessage);

      if (shouldForceLogout) {
        triggerClientForceLogout(
          forceLogoutReason ??
            (response.status === 401
              ? 'token_present_session_hydration_failed'
              : 'session_state_user_id_mismatch')
        );
      }
      return;
    }

    const data = (await response.json()) as {
      sessionId?: string;
      platform?: string;
      reused?: boolean;
    };

    if (data.sessionId && data.sessionId !== sessionId) {
      onServerSessionIdChanged(data.sessionId);
    }
    onMarkSeen(new Date().toISOString());
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Session sync failed; falling back to local id', error);
    }
  }
};

export const sendLifecycleEventSafely = async ({
  event,
  currentUserId,
  assignmentIdOverride,
  getSessionId,
  onExpired,
  preferBeacon = false,
  retryOnExpiry = true,
}: {
  event: SessionEvent;
  currentUserId: string | null | undefined;
  assignmentIdOverride?: string;
  getSessionId: () => string;
  onExpired: () => Promise<void>;
  preferBeacon?: boolean;
  retryOnExpiry?: boolean;
}): Promise<boolean> => {
  const eventName = `SESSION_${event.type}`;
  const lifecyclePayload = {
    event_name: eventName,
    lifecycle_payload: event.payload,
    prefer_beacon: preferBeacon,
  };

  try {
    await sendSessionStateEvents([
      {
        sessionId: getSessionId(),
        userId: currentUserId ?? undefined,
        clientTimestamp: event.timestamp,
        platform: 'WEB',
        assignmentId:
          assignmentIdOverride ?? getBrowserContext().assignmentId ?? undefined,
        severity: 'INFO',
        eventType: SessionStateEventType.SYSTEM_FLOW,
        eventName,
        eventPayload: {
          ...lifecyclePayload,
        },
      },
    ]);
    return true;
  } catch (error) {
    if (isSessionStateExpiredError(error) && retryOnExpiry) {
      await onExpired();
      const retriedEvent: SessionEvent = {
        ...event,
        sessionId: getSessionId(),
        timestamp: new Date().toISOString(),
      };
      return sendLifecycleEventSafely({
        event: retriedEvent,
        currentUserId,
        assignmentIdOverride,
        getSessionId,
        onExpired,
        preferBeacon,
        retryOnExpiry: false,
      });
    }
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Failed to send lifecycle event', error);
    }
    return false;
  }
};
